
import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { getCustomerDisplayName, getCustomerShortName, type CustomerDisplayData } from '@/utils/customerDisplayHelpers';

export interface SearchableCustomerOption extends CustomerDisplayData {
  id: string;
}

interface SearchableCustomerSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  customers?: SearchableCustomerOption[];
  placeholder?: string;
  allowNone?: boolean;
  noneLabel?: string;
  disabled?: boolean;
  className?: string;
}

const SearchableCustomerSelect: React.FC<SearchableCustomerSelectProps> = ({
  value,
  onValueChange,
  customers = [],
  placeholder = 'Select a customer',
  allowNone = false,
  noneLabel = 'No customer',
  disabled = false,
  className,
}) => {
  const [open, setOpen] = useState(false);

  const selectedCustomer = customers.find((c) => c.id === value);
  const displayValue = value === 'no-customer'
    ? noneLabel
    : selectedCustomer
      ? getCustomerShortName(selectedCustomer)
      : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !displayValue && 'text-muted-foreground',
            className
          )}
        >
          <span className="truncate">{displayValue || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search customers..." />
          <CommandList>
            <CommandEmpty>No customers found.</CommandEmpty>
            <CommandGroup>
              {allowNone && (
                <CommandItem
                  value={noneLabel}
                  onSelect={() => {
                    onValueChange('no-customer');
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === 'no-customer' ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {noneLabel}
                </CommandItem>
              )}
              {customers.map((customer) => {
                const label = getCustomerDisplayName(customer);
                const isCommercial = customer.customer_type === 'commercial';
                const primaryName = isCommercial && customer.business_name
                  ? customer.business_name
                  : `${customer.name || ''} ${customer.last_name || ''}`.trim() || 'Unnamed Customer';
                const repName = isCommercial && customer.business_name
                  ? `${customer.representative_first_name || customer.name || ''} ${customer.representative_last_name || customer.last_name || ''}`.trim()
                  : null;

                return (
                  <CommandItem
                    key={customer.id}
                    value={label}
                    onSelect={() => {
                      onValueChange(customer.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value === customer.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate">{primaryName}</span>
                      {repName && (
                        <span className="truncate text-xs text-muted-foreground">{repName}</span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableCustomerSelect;
